/**
 * S23 · Share sheet — receipt + multi-channel export for an approved
 * or submitted session. No PDF library is bundled; we lean on the
 * platform:
 *   - `navigator.share()` hands to the OS share sheet (AirDrop,
 *     iMessage, Slack, etc) when available.
 *   - `mailto:` for email fall-back.
 *   - `window.print()` against a print-only stylesheet gives the user
 *     a PDF via the native Print dialog.
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { useCapturesBySession, useSession } from '@kapture/client-core';
import { subjectCount } from '@kapture/domain';

export const Route = createFileRoute('/sessions/$sessionId/share')({
  component: SharePage,
  staticData: { mobile: true },
});

function SharePage() {
  const { sessionId } = Route.useParams();
  const { data: session } = useSession(sessionId);
  const { data: captures } = useCapturesBySession(sessionId);

  const receipt = (session?.meta['receipt'] as string | undefined) ?? '—';
  const signerName = (session?.meta['signerName'] as string | undefined) ?? 'Sarah Chen';
  const shareUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/sessions/${sessionId}` : '';

  // None of these three currently changes what is shared: `nativeShare` sends
  // a title, a line of text and a URL, and no attachment list is assembled
  // from them. They are recorded here so that when the share payload becomes
  // real, video is already represented rather than being the one kind nobody
  // remembered — but do not read a working filter into them today.
  const [includePhotos, setIncludePhotos] = useState(true);
  const [includeVideo, setIncludeVideo] = useState(false);
  const [includeVoice, setIncludeVoice] = useState(false);
  const [expireDays, setExpireDays] = useState(7);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  async function nativeShare() {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({
          title: session?.title ?? 'Kapture inspection',
          text: `${session?.title} · receipt ${receipt}`,
          url: shareUrl,
        });
      } catch {
        // user cancelled; no-op
      }
    } else {
      void copyLink();
    }
  }

  async function copyLink() {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1400);
    }
  }

  function email() {
    const subject = encodeURIComponent(session?.title ?? 'Kapture inspection');
    const body = encodeURIComponent(`Receipt ${receipt}\nSigned by ${signerName}\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  function print() {
    window.print();
  }

  // How many things this covers, beside how much of it there is. The receipt
  // line is the only place the export says what it contains, and "27 captures"
  // reads the same for one door photographed twenty-seven times as for
  // twenty-seven doors — a difference the recipient cannot recover from the
  // form, which carries a single door id for the whole session. Zero when
  // nothing was captured against a code, which is every session so far, and
  // the line then reads exactly as it always has.
  const subjects = subjectCount(captures ?? []);

  const photoCount = captures?.filter((c) => c.kind === 'photo').length ?? 0;
  const videoCount = captures?.filter((c) => c.kind === 'video').length ?? 0;
  const voiceCount = captures?.filter((c) => c.kind === 'voice').length ?? 0;

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <Link
          to="/sessions/$sessionId"
          params={{ sessionId }}
          aria-label="Back"
          style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
        >
          <Ico d={I.back} size={20} />
        </Link>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Share report</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {session?.title ?? 'Loading…'}
          </div>
        </div>
        <Chip tone="brandSoft" size="sm">
          {receipt}
        </Chip>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div
          style={{
            padding: 14,
            borderRadius: 14,
            border: `1px solid ${T.stroke}`,
            background: T.surface,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginBottom: 16,
            boxShadow: T.sh,
          }}
        >
          <div
            style={{
              width: 58,
              height: 74,
              borderRadius: 8,
              background: T.ink,
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              flexShrink: 0,
              fontFamily: T.mono,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          >
            <Ico d={I.file} size={22} />
            PDF
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: T.font }}>
              Inspection report
            </div>
            <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>
              {subjects > 0 && `${subjects} subject${subjects === 1 ? '' : 's'} · `}
              {captures?.length ?? 0} captures · signed by {signerName}
            </div>
            <div style={{ marginTop: 4 }}>
              <Chip tone="good" size="sm">
                Receipt {receipt}
              </Chip>
            </div>
          </div>
        </div>

        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 8,
          }}
        >
          Send to
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10,
            marginBottom: 16,
          }}
        >
          <ShareTile label="Share" icon={I.send} onClick={() => void nativeShare()} tone="brand" />
          <ShareTile label="Email" icon={I.note} onClick={email} />
          <ShareTile
            label={copyState === 'copied' ? 'Copied' : 'Copy link'}
            icon={copyState === 'copied' ? I.check2 : I.link}
            onClick={() => void copyLink()}
          />
          <ShareTile label="Print" icon={I.download} onClick={print} />
        </div>

        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 8,
          }}
        >
          Options
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <Toggle
            label={`Include raw photos (${photoCount})`}
            checked={includePhotos}
            onChange={setIncludePhotos}
          />
          <Toggle
            label={`Include video clips (${videoCount})`}
            checked={includeVideo}
            onChange={setIncludeVideo}
          />
          <Toggle
            label={`Include voice notes (${voiceCount})`}
            checked={includeVoice}
            onChange={setIncludeVoice}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: '10px 14px',
              border: `1px solid ${T.stroke}`,
              borderRadius: 10,
              background: T.surface,
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, fontFamily: T.font }}>Link expires</div>
              <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
                Receiver loses access after
              </div>
            </div>
            <select
              value={expireDays}
              onChange={(e) => setExpireDays(Number(e.target.value))}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: `1px solid ${T.stroke}`,
                background: T.surface,
                fontFamily: T.mono,
                fontSize: 12,
                color: T.text,
              }}
            >
              <option value={1}>1 day</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={0}>Never</option>
            </select>
          </div>
        </div>

        <div
          style={{
            fontSize: 11,
            color: T.textMute,
            fontFamily: T.font,
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          A signed record stays in this device's local archive regardless. Share links are tracked
          in the audit log when the backend lands.
        </div>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function ShareTile({
  label,
  icon,
  onClick,
  tone,
}: {
  label: string;
  icon: string | React.ReactNode;
  onClick(): void;
  tone?: 'brand';
}) {
  const brand = tone === 'brand';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '14px 8px 10px',
        borderRadius: 14,
        border: `1px solid ${brand ? T.brand : T.stroke}`,
        background: brand ? T.brand : T.surface,
        color: brand ? '#fff' : T.text,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        fontFamily: T.font,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 10,
          background: brand ? 'rgba(255,255,255,0.18)' : T.surface2,
          color: brand ? '#fff' : T.textDim,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Ico d={icon} size={15} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
    </button>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        border: `1px solid ${T.stroke}`,
        borderRadius: 10,
        background: T.surface,
        fontFamily: T.font,
        color: T.text,
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 600 }}>{label}</div>
      <div
        style={{
          width: 40,
          height: 24,
          borderRadius: 12,
          background: checked ? T.brand : T.stroke,
          position: 'relative',
          transition: 'background 120ms ease',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 20,
            height: 20,
            borderRadius: 10,
            background: '#fff',
            transition: 'left 140ms ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      </div>
    </button>
  );
}
